/**
 * List-search helpers.
 *
 * ACCURACY RULE: a list's text search must match the strings the user actually
 * SEES in each row — never a hidden underlying value. The list rows show a short
 * id (`id.slice(0, 8)`, rendered uppercased), not the full 36-char UUID. Matching
 * against the full UUID makes an id search (e.g. "A7") match rows whose visible id
 * does NOT contain the term — because "a7" appears somewhere in the UUID's hidden
 * tail — so the list fails to narrow. These helpers search the DISPLAYED short id.
 */
import type { EstimateSummary, InvoiceSummary } from '@beam/api-types';

/** The visible short id shown in list rows (id.slice(0, 8), rendered uppercased). */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** Case-insensitive substring match of `q` (already trimmed+lowercased) against a row's fields. */
function anyFieldMatches(fields: Array<string | null | undefined>, q: string): boolean {
  return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
}

type EstimateSearchRow = Pick<EstimateSummary, 'id' | 'status' | 'approvalStatus'>;

/** Filter estimates by the text visible in each row (short id, status, approval status). */
export function filterEstimates<T extends EstimateSearchRow>(rows: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((e) => anyFieldMatches([shortId(e.id), e.status, e.approvalStatus], q));
}

type InvoiceSearchRow = Pick<InvoiceSummary, 'id' | 'invoiceNumber' | 'status'>;

/** Filter invoices by the text visible in each row (invoice number or short id, status). */
export function filterInvoices<T extends InvoiceSearchRow>(rows: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((i) => anyFieldMatches([i.invoiceNumber, shortId(i.id), i.status], q));
}
